/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import '@xyflow/react/dist/style.css';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Drawer,
  Menu,
  MenuItem,
  Button,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  addEdge,
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useNodesInitialized,
  useReactFlow,
  Handle,
  Position,
} from '@xyflow/react';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { backTarget, returnToCurrentFlow } from './flowEditorNavigation';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import StorageIcon from '@mui/icons-material/Storage';
import InputIcon from '@mui/icons-material/Input';
import OutputIcon from '@mui/icons-material/Output';
import TransformIcon from '@mui/icons-material/Transform';
import VectorizeIcon from '@mui/icons-material/LinearScale';
import DecisionIcon from '@mui/icons-material/Assessment';
import ContentIcon from '@mui/icons-material/Description';
import GenerateIcon from '@mui/icons-material/AutoAwesome';
import FolderIcon from '@mui/icons-material/Folder';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ViewListIcon from '@mui/icons-material/ViewList';
import SendIcon from '@mui/icons-material/Send';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import axios from 'axios';
import { environment } from '../../environments/environment';
import { useNavigate, useLocation } from 'react-router-dom';
import { ButtonK } from '@datascient/datascient-ui-library';
import ChipInput from 'datascient-ui-library/src/lib/Molecules/MultiInputField/MultiInput';
import dagre from 'dagre';
import AdminActivityTemplates from './AdminActivityTemplates';
import axiosInstance from '../api';
import ChatAssistance from './ChatAssistance';
import { ScopeBadge, ModeLabelChip, getNodeGlyph, getInteractionMode } from './flowNodeVisuals';
import IntegrationBindingCard from './IntegrationBindingCard';
import IntegrationExplorerDrawer from './IntegrationExplorerDrawer';
import TaskConfigurationForm from './TaskConfigurationForm';
import FlowRunPanel from './FlowRunPanel';
import { benchmarkService } from '../services/benchmarkService';
import { benchmarkFamily, benchmarkFlowCode, suppliedSampleFamilies, validateFlowCode } from './Benchmark/benchmarkFlowCodes';
import { refusalGuidance } from './Benchmark/supplied/refusalGuidance';
import SuppliedSampleWizard from './Benchmark/supplied/SuppliedSampleWizard';
import {
  MAIN_BRANCH,
  branchLabel,
  buildActivityNodeMap,
  deriveRunState,
  extractReasoningEntries,
  resolveEntryNode,
} from './flowRunMapping';
import { AgentRunBadge } from './ExecutionStats/ExecutionStatsBadge';
import LastSuccessfulRun from './ExecutionStats/LastSuccessfulRun';
import { dsVar, ink } from '../theme/tokens';

/** Safety cap: tear the run screen down if the stream goes quiet this long. */
const REASONING_IDLE_TIMEOUT_MS = 600000;

const nodeWidth = 280;
const nodeHeight = 80;

// Task type configurations
const taskTypeConfig = {
  'CONTENT_RETRIEVER': {
    icon: <ContentIcon />,
    color: '#4CAF50',
    bgColor: 'rgba(76, 175, 80, 0.1)',
  },
  'INPUT_PROCESSOR': {
    icon: <InputIcon />,
    color: '#2196F3',
    bgColor: 'rgba(33, 150, 243, 0.1)',
  },
  'OUTPUT_PROCESSOR': {
    icon: <OutputIcon />,
    color: '#FF9800',
    bgColor: 'rgba(255, 152, 0, 0.1)',
  },
  'FEATURE_EXTRACTOR': {
    icon: <TransformIcon />,
    color: '#9C27B0',
    bgColor: 'rgba(156, 39, 176, 0.1)',
  },
  'FEATURE_VECTORIZER': {
    icon: <VectorizeIcon />,
    color: '#E91E63',
    bgColor: 'rgba(233, 30, 99, 0.1)',
  },
  'DATA_TRANSFORMER': {
    icon: <TransformIcon />,
    color: '#00BCD4',
    bgColor: 'rgba(0, 188, 212, 0.1)',
  },
  'DECISION_AGENT': {
    icon: <DecisionIcon />,
    color: '#F44336',
    bgColor: 'rgba(244, 67, 54, 0.1)',
  },
  'CONTENT_GENERATOR': {
    icon: <GenerateIcon />,
    color: '#673AB7',
    bgColor: 'rgba(103, 58, 183, 0.1)',
  },
  'Uncategorized': {
    icon: <FolderIcon />,
    color: dsVar('fg-muted'),
    bgColor: 'rgba(96, 125, 139, 0.1)',
  },
};

const toTitleCase = (str) => {
  if (!str) return 'Uncategorized';
  return str.toLowerCase().split(/[_\s]+/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const nodeStyles = `
  .react-flow__node {
    border-radius: 24px;
    padding: 0;
    background: transparent;
    border: none;
    color: rgb(var(--ds-fg-body));
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .react-flow__node:hover {
    border-color: rgb(var(--ds-accent-fg));
    box-shadow: 0 0 15px rgba(255, 222, 89, 0.2);
    transform: translateY(-2px);
  }
  .react-flow__handle {
    width: 14px;
    height: 14px;
    background: rgb(var(--ds-accent));
    border: 3px solid rgb(var(--ds-page));
    box-shadow: 0 0 6px rgba(255, 222, 89, 0.4);
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .react-flow__handle:hover {
    width: 20px;
    height: 20px;
    background: #FFF;
    box-shadow: 0 0 12px rgba(255, 222, 89, 0.9);
  }
  .react-flow__edge-path {
    stroke: rgb(var(--ds-accent-fg));
    stroke-width: 2.5;
    filter: drop-shadow(0 0 3px rgba(255, 222, 89, 0.4));
  }
  /* Dark gold on white needs its full strength; the dimmed edges read on navy only. */
  [data-theme="light"] .react-flow__edge-path {
    opacity: 1 !important;
  }
  .react-flow__panel.react-flow__minimap {
    margin: 0 !important;
    top: auto !important;
    left: auto !important;
    right: 0 !important;
    bottom: 0 !important;
    border-bottom-right-radius: 0 !important;
    border-top-right-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    transform: none !important;
  }
  .react-flow__controls {
    background: rgb(var(--ds-surface));
    border: 1px solid rgb(var(--ds-line));
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }
  .react-flow__controls-button {
    background: rgb(var(--ds-surface));
    color: rgb(var(--ds-fg-body));
    border-bottom: 1px solid rgb(var(--ds-line));
    fill: currentColor;
  }
  /* Read-only drawer: keep text legible when fields are disabled */
  .MuiInputBase-input.Mui-disabled {
    -webkit-text-fill-color: rgb(var(--ds-fg-soft)) !important;
    opacity: 1 !important;
  }
  .MuiInputBase-root.Mui-disabled {
    background-color: rgb(var(--ds-page)) !important;
  }
  /* ── Typography: JetBrains Mono for headings, Inter for body ── */
  .MuiTypography-root {
    font-family: 'Inter', sans-serif !important;
  }
  .MuiTypography-h1,
  .MuiTypography-h2,
  .MuiTypography-h3,
  .MuiTypography-h4,
  .MuiTypography-h5,
  .MuiTypography-h6 {
    font-family: 'JetBrains Mono', monospace !important;
  }
  /* Catalog & panel section labels (uppercase caps labels rendered as body) */
  .agent-catalog-header,
  .MuiAccordionSummary-content .MuiTypography-root {
    font-family: 'JetBrains Mono', monospace !important;
  }
  /* Drawer & page title h6 overrides */
  .MuiDrawer-paper .MuiTypography-h6 {
    font-family: 'JetBrains Mono', monospace !important;
  }
  /* Input text stays Inter */
  .MuiInputBase-root,
  .MuiInputBase-input {
    font-family: 'Inter', sans-serif !important;
  }
  /* ── Drawer-specific (portal-rendered) font guarantees ── */
  .MuiDrawer-paper .MuiTypography-root {
    font-family: 'Inter', sans-serif !important;
  }
  .MuiDrawer-paper .MuiTypography-h4,
  .MuiDrawer-paper .MuiTypography-h5,
  .MuiDrawer-paper .MuiTypography-h6 {
    font-family: 'JetBrains Mono', monospace !important;
  }
  .MuiDrawer-paper .MuiInputBase-root,
  .MuiDrawer-paper .MuiInputBase-input,
  .MuiDrawer-paper .MuiSelect-select {
    font-family: 'Inter', sans-serif !important;
  }
  .MuiDrawer-paper .MuiTableCell-root {
    font-family: 'Inter', sans-serif !important;
  }
  /* Dropdown menu list items */
  .MuiPopover-root .MuiMenuItem-root {
    font-family: 'Inter', sans-serif !important;
  }
  /* ── ReactFlow canvas: node & edge label fonts ── */
  .react-flow__node,
  .react-flow__node * {
    font-family: 'Inter', sans-serif !important;
  }
  .react-flow__edge-textwrapper,
  .react-flow__edge-label {
    font-family: 'Inter', sans-serif !important;
  }
  /* ── Run state: pulsing ring on the activity currently executing ── */
  /* The ring lives on a ::after pseudo-element, not on the node's own
     box-shadow. A keyframe animation overrides the element's normal box-shadow
     for its whole duration, so animating it directly stripped the node's base
     depth shadow and left the *active* node looking flatter than its neighbours
     — the opposite of emphasis. */
  @keyframes agentRunRing {
    0%   { transform: scale(1);    opacity: 0.9; }
    70%  { transform: scale(1.14); opacity: 0; }
    100% { transform: scale(1.14); opacity: 0; }
  }
  .agent-node-running {
    position: relative;
  }
  .agent-node-running::after {
    content: '';
    position: absolute;
    inset: -5px;
    border: 2px solid rgb(var(--ds-accent-fg));
    border-radius: 28px;
    pointer-events: none;
    animation: agentRunRing 1.6s ease-out infinite;
  }
  /* Spotlight: everything that isn't the active node recedes while a run
     streams, so the active node reads by contrast rather than by piling on
     more glow. Survives being zoomed out, which glow alone does not. */
  .agent-node-dimmed {
    opacity: 0.4;
    filter: saturate(0.7);
    transition: opacity 0.3s ease, filter 0.3s ease;
  }
  /* Small standalone indicator (the canvas "Run in progress" chip). */
  @keyframes agentDotPulse {
    0%, 100% { opacity: 1; }
    50%      { opacity: 0.3; }
  }
  .agent-dot-pulse {
    animation: agentDotPulse 1.4s ease-in-out infinite;
  }
  /* ── Run panel: terminal-style caret on the line currently streaming ── */
  @keyframes agentCaretBlink {
    0%, 49%   { opacity: 1; }
    50%, 100% { opacity: 0; }
  }
  .agent-run-caret {
    display: inline-block;
    width: 6px;
    height: 11px;
    margin-left: 3px;
    vertical-align: -1px;
    background: rgb(var(--ds-accent));
    animation: agentCaretBlink 1s step-end infinite;
  }
  /* Entry animation for streamed reasoning lines. No stagger delay: CSS
     animations fire on mount and React reuses existing DOM nodes, so only the
     newly arrived line animates and the stream's own cadence is the stagger. */
  @keyframes agentEntryIn {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: none; }
  }
  .agent-run-entry {
    animation: agentEntryIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  /* Run-panel text.
     These are compound selectors (.MuiTypography-root.x) on purpose. This same
     stylesheet declares an unscoped ".MuiTypography-root { font-family: Inter
     !important }" above, which ties with any single-class rule and then wins on
     source order — that is why the mono styling silently did nothing before.
     Raising the specificity makes these independent of rule order and of which
     file they live in. */
  .MuiTypography-root.agent-run-mono {
    font-family: 'JetBrains Mono', monospace !important;
    font-size: 10.5px !important;
    line-height: 1.7 !important;
    letter-spacing: 0.01em !important;
    color: rgb(var(--ds-fg-2)) !important;
  }
  .MuiTypography-root.agent-run-step {
    font-family: 'Inter', sans-serif !important;
    font-size: 11.5px !important;
    font-weight: 600 !important;
    color: rgb(var(--ds-fg-soft)) !important;
  }
`;

/** Frame accents applied while a run is in flight. `undefined` renders as before. */
const runStateVisuals = {
  running: { color: dsVar('accent-fg'), badge: null },
  done: { color: '#10B981', badge: '✓' },
  failed: { color: '#F87171', badge: '!' },
};

const CustomFlowNode = ({ data }) => {
  const isSubFlow = data.linked_flow_template_code && data.linked_flow_template_code !== '';
  // Populated only while a run is streaming; see deriveRunState in flowRunMapping.
  const run = data.runState;
  const runVisual = run ? runStateVisuals[run.status] : null;
  const isRunningNode = run?.status === 'running';
  // Only nodes the run hasn't reached recede. Completed nodes stay at full
  // opacity — they're the progress trail, and dimming them would throw away the
  // most useful thing on the canvas.
  const isDimmed = data.runActive && !run;
  const runClass = [
    isRunningNode ? 'agent-node-running' : '',
    isDimmed ? 'agent-node-dimmed' : '',
  ].filter(Boolean).join(' ') || undefined;
  // Human-control posture (interaction_mode) drives the node's frame accent,
  // left-edge colour and mode chip. All nodes keep the same card shape; gateway
  // modes (APPROVAL_REQUIRED / HUMAN_ONLY) are distinguished by their coral/navy
  // accent, mode chip and a gate icon in the main icon slot.
  const im = getInteractionMode(data.interaction_mode);
  const NodeGlyph = getNodeGlyph(data.interaction_mode, SmartToyIcon);
  return (
    <Box className={runClass} sx={{
      padding: isRunningNode ? '3px' : '2px',
      borderRadius: '24px',
      background: runVisual
        ? isRunningNode
          ? runVisual.color
          : `linear-gradient(135deg, ${runVisual.color} 0%, ${runVisual.color}55 50%, ${runVisual.color} 100%)`
        : isSubFlow
          ? 'linear-gradient(135deg, #7C3AED 0%, #3B82F6 50%, #7C3AED 100%)'
          : `linear-gradient(135deg, ${im.stroke}66 0%, transparent 55%, ${im.stroke}66 100%)`,
      transition: 'all 0.3s ease',
      // Steady glow carries the emphasis at any zoom; the ::after ring adds the
      // motion on top of it.
      boxShadow: isRunningNode
        ? '0 8px 28px rgba(0,0,0,0.45), 0 0 0 3px rgba(255, 222, 89, 0.2), 0 0 34px rgba(255, 222, 89, 0.5)'
        : isSubFlow
          ? '0 8px 32px rgba(0,0,0,0.4), 0 0 20px rgba(124, 58, 237, 0.25)'
          : '0 8px 28px rgba(0,0,0,0.4)',
      border: isSubFlow ? '2px dashed rgba(124, 58, 237, 0.5)' : 'none',
      '&:hover': {
        transform: 'translateY(-4px)',
        zIndex: 100,
        boxShadow: isSubFlow
          ? '0 12px 48px rgba(0,0,0,0.6), 0 0 30px rgba(124, 58, 237, 0.45)'
          : '0 12px 44px rgba(0,0,0,0.6)',
      }
    }}>
      <Box sx={{
        padding: '14px 18px',
        borderRadius: '22px',
        background: isSubFlow ? dsVar('subflow') : dsVar('page'),
        border: isSubFlow ? `1px solid ${dsVar('fg', 0.03)}` : `${im.weight} ${im.border} ${im.stroke}`,
        width: nodeWidth - 2,
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} />
        <ModeLabelChip mode={data.interaction_mode} />
        <ScopeBadge scope={data.scope} />
        <Box sx={{
          color: isSubFlow ? ink('#A78BFA') : im.stroke,
          backgroundColor: isSubFlow ? 'rgba(124, 58, 237, 0.15)' : `${im.fill}33`,
          borderLeft: isSubFlow ? 'none' : `3px solid ${im.fill}`,
          borderRadius: '8px',
          padding: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          {isSubFlow ? <AccountTreeIcon /> : <NodeGlyph />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontWeight: 600,
            fontSize: '12.5px',
            color: dsVar('fg'),
            lineHeight: 1.4,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            fontFamily: "'Inter', sans-serif",
          }}>
            {data.activity_template_name || data.activityTemplateName}
          </Typography>
          {/* Historical run counts, hidden during a live run so its state
              is the only thing the node shows. */}
          {environment.executionStatsNodeScope && !data.runActive && !isSubFlow && data.activity_template_code && (
            <Box sx={{ mt: 0.5, lineHeight: 1 }}>
              <AgentRunBadge activityTemplateCode={data.activity_template_code} />
            </Box>
          )}
        </Box>
        {runVisual?.badge && (
          <Box sx={{
            position: 'absolute',
            top: -10,
            right: -10,
            width: 24,
            height: 24,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '13px',
            fontWeight: 800,
            lineHeight: 1,
            color: dsVar('on-accent'),
            backgroundColor: runVisual.color,
            border: `2px solid ${dsVar('page')}`,
            boxShadow: `0 0 12px ${runVisual.color}80`,
          }}>
            {runVisual.badge}
          </Box>
        )}
        {/* Fan-out counter. The denominator is branches *seen so far* — fan-out
            width is data-dependent and unknown until each branch first emits. */}
        {run && run.observedBranches > 1 && (
          <Chip
            label={`${run.done} / ${run.observedBranches} branches`}
            size="small"
            title="Branches seen so far in this run"
            sx={{
              position: 'absolute',
              bottom: -10,
              left: 12,
              height: '18px',
              fontSize: '9px',
              fontWeight: 700,
              backgroundColor: 'rgba(255, 222, 89, 0.15)',
              color: dsVar('accent-fg'),
              border: '1px solid rgba(255, 222, 89, 0.4)',
              borderRadius: '4px',
              '& .MuiChip-label': { px: '6px' },
            }}
          />
        )}
        {isSubFlow && (
          <Chip label="Sub-flow" size="small" sx={{
            position: 'absolute',
            bottom: -10,
            right: 12,
            height: '18px',
            fontSize: '9px',
            fontWeight: 700,
            letterSpacing: '0.04em',
            backgroundColor: 'rgba(124, 58, 237, 0.2)',
            color: ink('#A78BFA'),
            border: '1px solid rgba(124, 58, 237, 0.4)',
            borderRadius: '4px',
            '& .MuiChip-label': { px: '6px' },
          }} />
        )}
        <Handle type="source" position={Position.Right} />
      </Box>
    </Box>
  );
};

const nodeTypes = { workflowNode: CustomFlowNode };

// Refits the viewport whenever a new flow is loaded. Required for drill-down:
// navigating to /AIStudio/customize with a different flow_template_code reloads
// the nodes without remounting, so the <ReactFlow fitView /> prop (which only
// runs on initial mount) never re-centers the sub-flow. We re-fit any time
// nodesInitialized reads true for a new flowKey rather than only once — a
// stale (still-true) nodesInitialized reading from the previous flow can
// otherwise race the flowKey change, firing a premature fit and permanently
// skipping the real one once the new nodes finish measuring.
const FitViewOnFlowChange = ({ flowKey }) => {
  const { fitView } = useReactFlow();
  const nodesInitialized = useNodesInitialized();

  useEffect(() => {
    if (nodesInitialized) {
      fitView({ padding: 0.2, duration: 300 });
    }
  }, [flowKey, nodesInitialized, fitView]);

  return null;
};

// Follows the run: centres the viewport on each newly active node. Holds the
// current zoom rather than letting setCenter pick one, which would yank the
// user's zoom level around on every step. `enabled` goes false the moment the
// user pans or zooms, so this never fights them for the viewport.
const FocusRunningNode = ({ nodeId, enabled }) => {
  const { setCenter, getNode, getZoom } = useReactFlow();
  const lastNodeRef = useRef(null);

  useEffect(() => {
    // A new run clears the entries, so reset here — otherwise a run whose first
    // node matches the previous run's last node would never pan to it.
    if (!nodeId) {
      lastNodeRef.current = null;
      return undefined;
    }
    if (!enabled || nodeId === lastNodeRef.current) return undefined;
    const node = getNode(nodeId);
    if (!node) return undefined;
    lastNodeRef.current = nodeId;
    // Deferred a frame so the viewport change never lands inside a ResizeObserver
    // notification while the panel/catalog widths are animating.
    const raf = requestAnimationFrame(() => {
      setCenter(
        node.position.x + nodeWidth / 2,
        node.position.y + nodeHeight / 2,
        { zoom: getZoom(), duration: 400 }
      );
    });
    return () => cancelAnimationFrame(raf);
  }, [nodeId, enabled, setCenter, getNode, getZoom]);

  return null;
};

// Re-fits after the canvas changes width (run panel or catalog animating), once
// the 250ms transition has settled. Skipped while auto-pan owns the viewport so
// the two don't fight, and on first mount because <ReactFlow fitView /> covers it.
const RefitOnLayoutChange = ({ trigger, skip }) => {
  const { fitView } = useReactFlow();
  const isFirstRef = useRef(true);

  useEffect(() => {
    if (isFirstRef.current) {
      isFirstRef.current = false;
      return undefined;
    }
    if (skip) return undefined;
    // 320ms clears the 250ms width transition with margin, so the fit runs after
    // the container has settled rather than while it is still being resized.
    let raf = 0;
    const id = setTimeout(() => {
      raf = requestAnimationFrame(() => fitView({ padding: 0.2, duration: 300 }));
    }, 320);
    return () => {
      clearTimeout(id);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [trigger, skip, fitView]);

  return null;
};

const FlowDropTarget = ({ onDrop, children }) => {
  const { screenToFlowPosition } = useReactFlow();
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = (e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('application/reactflow-template');
    if (!raw) return;
    const template = JSON.parse(raw);
    const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    onDrop(template, position);
  };
  return (
    <Box onDragOver={handleDragOver} onDrop={handleDrop} sx={{ width: '100%', height: '100%' }}>
      {children}
    </Box>
  );
};

const connectionLineStyle = {
  stroke: dsVar('accent-fg'),
  strokeWidth: 2,
  strokeDasharray: '5,5'
};

const edgeOptions = {
  animated: true,
  style: {
    stroke: dsVar('accent-fg'),
    strokeWidth: 2,
  },
  type: 'smoothstep',
  markerEnd: {
    type: 'arrowclosed',
    color: dsVar('accent-fg'),
    width: 15,
    height: 15,
  },
};

const getLayoutedElements = (nodes, edges) => {
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: 'LR',
    align: 'UL',
    nodesep: 50,
    ranksep: 100,
    marginx: 50,
    marginy: 50
  });
  g.setDefaultEdgeLabel(() => ({}));
  nodes.forEach((node) => g.setNode(node.id, { width: nodeWidth, height: nodeHeight }));
  edges.forEach((edge) => g.setEdge(edge.source, edge.target));
  dagre.layout(g);
  return {
    nodes: nodes.map((node) => {
      const nodeWithPosition = g.node(node.id);
      node.position = {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      };
      return node;
    }),
    edges,
  };
};

const AgentFlowEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [text1, setText1] = useState('Untitled Flow');
  const [activitytemplates, setActivitytemplates] = useState();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [flowTemplateDetails, setFlowTemplateDetails] = useState();
  // Bumped on every successful flow load so the canvas re-fits on drill-down.
  const [flowLoadSeq, setFlowLoadSeq] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [edgeMenuPosition, setEdgeMenuPosition] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [cameFromOperator, setCameFromOperator] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [editNodeDetails, setEditNodeDetails] = useState(null);
  const [editNodeConfig, setEditNodeConfig] = useState(null);
  const [isIntegrationDrawerOpen, setIsIntegrationDrawerOpen] = useState(false);
  const [isEdgeEditDrawerOpen, setIsEdgeEditDrawerOpen] = useState(false);
  const [editEdgeDetails, setEditEdgeDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDrawerReadOnly, setIsDrawerReadOnly] = useState(true);
  const [isEdgeDrawerReadOnly, setIsEdgeDrawerReadOnly] = useState(true);
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  // Hover preview state
  const [hoverPreview, setHoverPreview] = useState(null);
  const [previewPos, setPreviewPos] = useState(null);
  const previewRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  // Catalog panel view: 'assistant' (default) | 'list'
  const [catalogView, setCatalogView] = useState('assistant');

  // ── Run state ──
  // campaignId/projectId only exist when the editor was opened from a campaign
  // (ProjectReports / CampaignInsights). Run is disabled without them.
  const [campaignId, setCampaignId] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [runStatus, setRunStatus] = useState('idle'); // idle|starting|running|success|failure
  const [interactionId, setInteractionId] = useState(null);
  const [reasoningEntries, setReasoningEntries] = useState([]);
  const [runError, setRunError] = useState(null);
  const [isRunPanelOpen, setIsRunPanelOpen] = useState(false);
  const [isSavingBeforeRun, setIsSavingBeforeRun] = useState(false);
  const [latestBenchmarkRun, setLatestBenchmarkRun] = useState(null);
  const [isCheckingRun, setIsCheckingRun] = useState(false);
  // Supplied samples: the wizard, the run being followed (by its reference id,
  // which becomes the benchmark run's session), the run it produced, and a
  // counter the Backtest panel refreshes its list on.
  const [isSampleWizardOpen, setIsSampleWizardOpen] = useState(false);
  const activeSuppliedRef = useRef(null);
  const [completedSuppliedRunKey, setCompletedSuppliedRunKey] = useState(null);
  // Why a supplied sample finished without a run: the loader's refusal (or a
  // preview), shown in the run panel with what to do about it.
  const [suppliedNotScored, setSuppliedNotScored] = useState(null);
  const [suppliedRunsVersion, setSuppliedRunsVersion] = useState(0);
  const [runTimes, setRunTimes] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [activityNodeMap, setActivityNodeMap] = useState(() => new Map());
  const [isDirty, setIsDirty] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  // The catalog panel isn't useful mid-run, so it yields its width to the
  // squeezed canvas. Held as state (not derived from isRunning) so the chevron
  // can override it during a run.
  const [catalogCollapsed, setCatalogCollapsed] = useState(false);
  // Flipped once the user takes the viewport; stops auto-pan for the rest of
  // the run. ReactFlow passes a null event for its own programmatic moves.
  const [userMovedViewport, setUserMovedViewport] = useState(false);
  // Flow templates fetched while expanding sub-flows, keyed by code, so
  // drill-down and repeat runs don't refetch.
  const flowTemplateCacheRef = useRef(new Map());

  const isRunning = runStatus === 'starting' || runStatus === 'running';

  const showSnackbar = (message, severity = 'error') => setSnackbar({ open: true, message, severity });

  // "ResizeObserver loop completed with undelivered notifications" is a benign
  // browser notice, not an error: the observer could not settle within one frame
  // so the browser deferred the rest to the next one. ReactFlow observes its
  // container, and the run panel / catalog width transitions resize that
  // container on every frame of the animation, which is enough to trigger it.
  //
  // Nothing is broken, but webpack-dev-server's overlay treats any window error
  // as fatal and covers the page. Swallow this one message so the overlay stops
  // misreporting it. Deliberately narrow: it matches only this text, runs only
  // while the editor is mounted, and every other error passes straight through.
  useEffect(() => {
    const onError = (event) => {
      if (typeof event.message !== 'string' || !event.message.includes('ResizeObserver loop')) return;
      event.stopImmediatePropagation();
      event.preventDefault();
    };
    // Capture phase so this runs before the dev-server overlay's own listener.
    window.addEventListener('error', onError, true);
    return () => window.removeEventListener('error', onError, true);
  }, []);

  useEffect(() => {
    const flowTemplateCode = location.state?.flow_template_code;
    const operatorName = location.state?.operator_name;
    const operatorId = location.state?.operator_id;

    setCampaignId(location.state?.campaign_id ?? null);
    setProjectId(location.state?.project_id ?? null);

    if (flowTemplateCode) {
      getFlowTemplateDetails({ flow_template_code: flowTemplateCode });
      if (operatorName) setText1(operatorName);
      if (operatorId || location.state?.fromOperator) setCameFromOperator(true);
    } else {
      getEditableActivityTemplateForOrg();
    }
  }, [location.state?.flow_template_code]);

  const getEditableActivityTemplateForOrg = async (scopeLevel) => {
    try {
      const res = await axios.get(`${environment.controlplaneUrl}/manage/activity-templates`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          params: { activity_class: 'AGENT', latest_only: true, ...(scopeLevel ? { scope_level: scopeLevel } : {}) },
        }
      );
      setActivitytemplates(res?.data?.activity_templates);
    } catch (error) {
      if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) navigate('/login');
    }
  };

  const getFlowTemplateDetails = async (rowData) => {
    try {
      const res = await axios.get(
        `${environment.controlplaneUrl}/manage/flow-templates/${encodeURIComponent(rowData.flow_template_code)}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const flowTemplate = res.data?.flow_template;
      getEditableActivityTemplateForOrg(flowTemplate?.scope_level);
      const { configuration } = flowTemplate;
      setFlowTemplateDetails(flowTemplate);
      const loadedNodes = configuration.nodes.map((node) => ({
        id: node.data.id,
        type: 'workflowNode',
        position: node.position || { x: 0, y: 0 },
        data: { ...node.data, label: node.data.activity_template_name || node.data.activityTemplateName },
      }));
      const loadedEdges = configuration.edges.map((edge) => ({
        id: `e${edge.data.source}-${edge.data.target}`,
        source: edge.data.source,
        target: edge.data.target,
        animated: true,
        type: 'smoothstep',
        style: { stroke: dsVar('accent-fg'), strokeWidth: 2.5, opacity: 0.6 },
        markerEnd: { type: 'arrowclosed', color: dsVar('accent-fg'), width: 18, height: 18 },
      }));
      const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(loadedNodes, loadedEdges);
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
      setFlowLoadSeq((seq) => seq + 1);
      setIsDirty(false);
    } catch (error) {
      if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) navigate('/login');
    }
  };

  /** Cached single-template fetch used when expanding sub-flows for the run map. */
  const fetchFlowTemplate = useCallback(async (code) => {
    const cache = flowTemplateCacheRef.current;
    if (cache.has(code)) return cache.get(code);
    const res = await axios.get(
      `${environment.controlplaneUrl}/manage/flow-templates/${encodeURIComponent(code)}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    );
    const flowTemplate = res.data?.flow_template;
    cache.set(code, flowTemplate);
    return flowTemplate;
  }, []);

  // Identity of the graph for mapping purposes: which activities are on the
  // canvas and which of them open sub-flows. Dragging a node doesn't change it,
  // so the map isn't rebuilt on every mouse move — but adding or removing one does.
  const nodeMapSignature = nodes
    .map((n) => `${n.id}:${n.data?.activity_template_code || ''}:${n.data?.linked_flow_template_code || ''}`)
    .join('|');

  // Resolve every activity code — including those nested inside sub-flows — to a
  // node on the current canvas. Built at flow-load rather than at run start so
  // the first Run isn't delayed by a fetch waterfall.
  useEffect(() => {
    if (!nodes.length) {
      setActivityNodeMap(new Map());
      return undefined;
    }
    let cancelled = false;
    buildActivityNodeMap(nodes, fetchFlowTemplate)
      .then((map) => { if (!cancelled) setActivityNodeMap(map); })
      .catch(() => { /* partial map is fine — unmapped codes just don't highlight */ });
    return () => { cancelled = true; };
  }, [nodeMapSignature, fetchFlowTemplate]);

  /**
   * Persists the flow (and its operator record) without navigating. Throws on
   * failure so callers can decide what to do; Save navigates away, Run continues
   * into execution.
   */
  const persistFlowTemplate = async () => {
    const updatedNodes = nodes.map((node) => {
      const { label, runState, runActive, ...dataWithoutLabel } = node.data;
      return { data: dataWithoutLabel };
    });
    const updatedEdges = edges.map((edge) => {
      const templateEdge = flowTemplateDetails?.configuration?.edges?.find(e => e.data?.source === edge.source && e.data?.target === edge.target);
      return {
        data: {
          source: edge.source,
          target: edge.target,
          ...(templateEdge?.data?.type && { type: templateEdge.data.type }),
          ...(templateEdge?.data?.properties && { properties: templateEdge.data.properties })
        }
      };
    });
    const existingCode = flowTemplateDetails?.flow_template_code;
    if (!existingCode) throw new Error('This workflow has no template code to save against.');

    const payload = {
      flow_template_name: text1,
      flow_template_description: flowTemplateDetails?.flow_template_description || '',
      configuration: { nodes: updatedNodes, edges: updatedEdges },
    };
    const res = await axiosInstance.put(
      `/flow-templates/${encodeURIComponent(existingCode)}`,
      payload
    );
    const flowTemplateCode = res.data?.flow_template?.flow_template_code || existingCode;
    if (flowTemplateCode) {
      await axiosInstance.post('/operators/upsert', { operator_name: text1, flow_template_code: flowTemplateCode });
    }
    setIsDirty(false);
    return flowTemplateCode;
  };

  /**
   * Returns to the page the editor was opened from.
   *
   * See flowEditorNavigation for why this is not navigate(-1).
   */
  const handleBack = () => {
    const target = backTarget(location.state);
    if (target) {
      navigate(target.pathname, target.state ? { state: target.state } : undefined);
      return;
    }
    navigate(-1);
  };

  /**
   * This editor's own location, for a page it opens to come back to.
   *
   * The name is taken from the field rather than from location.state: a rename
   * is not saved until Save, so returning through a page this editor opened
   * would otherwise reinstate the name the user had already changed.
   */
  const currentReturnTo = () => returnToCurrentFlow({
    flowTemplateCode: flowTemplateDetails?.flow_template_code,
    locationState: {
      ...location.state,
      operator_name: typeof text1 === 'string' && text1 ? text1 : location.state?.operator_name,
    },
    campaignId,
    projectId,
  });

  const updateFlowTemplateDetails = async () => {
    try {
      await persistFlowTemplate();
      handleBack();
    } catch (error) {
      if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) {
        navigate('/login');
        return;
      }
      showSnackbar(error?.response?.data?.error || error?.message || 'Could not save this workflow.');
    }
  };

  const generateInteractionId = () =>
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });

  /** Stops the run, drops the socket (clearing interactionId) and surfaces why. */
  const failRun = (message) => {
    setRunStatus('failure');
    setInteractionId(null);
    setRunError(message);
    setRunTimes((prev) => (prev ? { ...prev, endedAt: Date.now() } : prev));
  };

  /**
   * Runs the workflow against the campaign it was opened from — the same
   * `POST /agent/flow/execute` the campaign page's "Run agent workflow" fires.
   *
   * The server executes the *saved* template, so unsaved canvas edits are
   * persisted first; otherwise the user would be watching a run of something
   * other than what's on screen.
   */
  const fetchLatestBenchmarkRun = async () => {
    if (!flowTemplateDetails?.flow_template_code) return null;
    const baseCode = flowTemplateDetails.flow_template_code;
    const effectiveProjectId = projectId || location.state?.project_id || 'benchmark';

    try {
      // By family, so a run recorded under a published build's stamped code is found.
      const run = await benchmarkService.getCurrentRun({
        flowFamily: benchmarkFamily(baseCode),
        projectId: effectiveProjectId,
      });
      if (run && run.runKey) {
        setLatestBenchmarkRun(run);
        return run;
      }
    } catch (err) {
      console.log('No prior benchmark run found or error fetching run:', err?.message);
    }
    setLatestBenchmarkRun(null);
    return null;
  };

  useEffect(() => {
    if (flowTemplateDetails?.flow_template_code) {
      fetchLatestBenchmarkRun();
    }
  }, [flowTemplateDetails?.flow_template_code, projectId]);

  /**
   * What a supplied sample's finished run produced, asked by its reference id:
   * a scored run, or the reason it was not scored.
   *
   * The orchestrator logs a refused or previewed flow as a success, so the
   * socket's "success" says only that the flow stopped. /runs/by-reference
   * reads the loader's own output and says which. The retries cover the moment
   * between the flow finishing and its log saying so.
   */
  const resolveSuppliedRun = async (referenceId) => {
    if (!referenceId) return;
    for (let attempt = 0; attempt < 12; attempt += 1) {
      try {
        const state = await benchmarkService.getRunByReference(referenceId);
        if (state.status === 'complete') {
          setCompletedSuppliedRunKey(state.runKey);
          setSuppliedRunsVersion((v) => v + 1);
          return;
        }
        if (state.status === 'not_scored') {
          setRunStatus('failure');
          setSuppliedNotScored({
            reason: state.reason,
            preview: state.preview,
            failedStep: state.failedStep || null,
            advice: state.reason ? refusalGuidance(state.reason).advice : null,
          });
          return;
        }
      } catch (err) {
        console.warn('Looking up the supplied-sample run failed:', err?.message);
      }
      await new Promise((resolve) => setTimeout(resolve, 5000));
    }
    // Still running as far as the server knows; its results page keeps following it.
    setRunError('The results are taking longer than expected to appear. Open the results page to keep following this sample.');
  };

  /**
   * Opens a run's results.
   *
   * `runKeyOverride` is a supplied-sample run picked from the panel's list.
   * Without one, the supplied sample just run opens on its own results — by
   * reference while its run key is not yet known, never by falling back to the
   * institution's stock run — and otherwise the stock run does.
   */
  const handleViewBenchmarkResults = (runKeyOverride) => {
    const baseCode = flowTemplateDetails?.flow_template_code || '';
    const effectiveProj = projectId || location.state?.project_id || 'benchmark';

    // Safely extract numeric or string runKey; ignore React SyntheticEvent or other objects
    let explicitRunKey = null;
    if (typeof runKeyOverride === 'number' && !isNaN(runKeyOverride)) {
      explicitRunKey = runKeyOverride;
    } else if (typeof runKeyOverride === 'string' && runKeyOverride.trim() && runKeyOverride !== '[object Object]') {
      explicitRunKey = runKeyOverride.trim();
    }

    const suppliedReference = activeSuppliedRef.current?.referenceId || null;
    const supplied = Boolean(explicitRunKey) || Boolean(suppliedReference);
    const targetRunKey = explicitRunKey
      ?? (suppliedReference ? completedSuppliedRunKey : latestBenchmarkRun?.runKey)
      ?? null;

    let path = '/benchmark-dashboard';
    if (targetRunKey) {
      path = `/benchmark-dashboard/${targetRunKey}${supplied ? '?tab=results' : ''}`;
    } else if (suppliedReference) {
      path = `/benchmark-dashboard?ref=${encodeURIComponent(suppliedReference)}&tab=results`;
    }

    navigate(path, {
      state: {
        run_key: targetRunKey,
        // The stock run's id only describes the stock run.
        run_id: supplied ? null : (latestBenchmarkRun?.runId || null),
        flow_template_code: supplied ? validateFlowCode(baseCode) : benchmarkFlowCode(baseCode),
        base_flow_template_code: baseCode,
        project_id: effectiveProj,
        operator_name: typeof text1 === 'string' ? text1 : '',
        operator_id: location.state?.operator_id || null,
        campaign_id: campaignId || null,
        fromOperator: Boolean(location.state?.fromOperator),
        // The editor itself, not the editor's origin: Back on the dashboard
        // returns here, and Back here then continues to wherever this editor
        // was opened from.
        returnTo: currentReturnTo(),
      },
    });
  };

  /**
   * Resets the run panel and starts following a run's reasoning stream. Shared
   * by a backtest and a supplied-sample run, so both play out on the same
   * timeline and canvas.
   */
  const beginRunTracking = (runInteractionId) => {
    setRunError(null);
    setReasoningEntries([]);
    setSelectedBranch(null);
    setInteractionId(runInteractionId);
    setUserMovedViewport(false);
    mapWarnedRef.current = false;
    setRunTimes({ startedAt: Date.now() });
    setRunStatus('starting');
    setIsRunPanelOpen(true);
  };

  /** A supplied sample's full run was accepted by the wizard; follow it here. */
  const handleSuppliedRunStarted = ({ interactionId: runInteractionId, referenceId }) => {
    activeSuppliedRef.current = referenceId ? { referenceId } : null;
    setCompletedSuppliedRunKey(null);
    setSuppliedNotScored(null);
    beginRunTracking(runInteractionId);
  };

  /** Back to the upload to correct a refused sample. */
  const reopenSampleWizard = () => {
    setSuppliedNotScored(null);
    setIsSampleWizardOpen(true);
  };

  // The results page's "Fix and resubmit" returns here asking for the upload.
  useEffect(() => {
    if (location.state?.openSampleWizard && flowTemplateDetails?.flow_template_code) {
      setIsSampleWizardOpen(true);
      navigate(location.pathname, { replace: true, state: { ...location.state, openSampleWizard: false } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.openSampleWizard, flowTemplateDetails?.flow_template_code]);

  /**
   * Executes the flow against the benchmark dataset and streams live reasoning logs.
   *
   * `agentSelection` ({agents, mode}) narrows which specialist agents run, and so which
   * of their risk factors the benchmark compares; absent, every agent runs.
   */
  const executeFlowRun = async (agentSelection = null) => {
    if (isRunning) {
      setIsRunPanelOpen(true);
      return;
    }
    if (!flowTemplateDetails?.flow_template_code) return;

    let flowTemplateCode = flowTemplateDetails.flow_template_code;
    if (isDirty) {
      setIsSavingBeforeRun(true);
      try {
        flowTemplateCode = await persistFlowTemplate();
      } catch (error) {
        setIsSavingBeforeRun(false);
        if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) {
          navigate('/login');
          return;
        }
        showSnackbar(error?.response?.data?.error || error?.message || 'Could not save before running.');
        return;
      }
      setIsSavingBeforeRun(false);
    }

    const newInteractionId = generateInteractionId();
    activeSuppliedRef.current = null;
    setCompletedSuppliedRunKey(null);
    setSuppliedNotScored(null);
    beginRunTracking(newInteractionId);

    const baseCode = flowTemplateCode;
    const effectiveProjectId = projectId || location.state?.project_id || 'benchmark';

    const genericError = 'The run could not be started. Please try again.';
    const payload = {
      data: {
        sourceLink: '',
        sourceName: 'User Input',
        fileType: 'docs',
        contentType: 'GENERIC_DATASET',
        campaignId: campaignId || '',
        projectId: effectiveProjectId,
        // Only a real selection: a click handler may pass an event here.
        ...(agentSelection && Array.isArray(agentSelection.agents) ? { agentSelection } : {}),
      },
      user: {
        interactionId: newInteractionId,
        projectId: effectiveProjectId,
      },
      agent: { code: benchmarkFlowCode(baseCode) },
    };

    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      // This endpoint can report failure inside a 2xx body, so inspect the
      // payload before settling in to wait on the reasoning stream.
      const body = response?.data;
      const statusCode = Number(body?.status);
      const errorMessage = typeof body?.error === 'string' ? body.error.trim() : '';
      if ((Number.isFinite(statusCode) && statusCode >= 400) || errorMessage) {
        failRun(errorMessage || genericError);
      }
    } catch (error) {
      if (error?.response) {
        // The server actively rejected it — the flow never started.
        const respData = error.response.data;
        failRun(respData?.error || respData?.message || genericError);
      } else {
        // No HTTP response (network error/abort). The flow may already be
        // running and the websocket is a separate channel, so keep listening;
        // the idle timeout is the backstop.
        console.warn('Execute flow request got no response; keeping the reasoning stream open.');
      }
    }
  };

  /**
   * Invoked when the user clicks 'Test' in the top right panel.
   * If getCurrentRun(flowCode, projectId) resolves to a valid latest run:
   * 1. Do not directly invoke the /agent/flow/execute API again.
   * 2. Provide the user options to re-run the test or view the benchmark results.
   */
  const handleTestClick = async () => {
    if (isRunning) {
      setIsRunPanelOpen(true);
      return;
    }
    if (!flowTemplateDetails?.flow_template_code) return;

    setIsCheckingRun(true);
    let existingRun = latestBenchmarkRun;
    try {
      existingRun = await fetchLatestBenchmarkRun();
    } catch (err) {
      console.log('Error verifying existing benchmark run:', err?.message);
    } finally {
      setIsCheckingRun(false);
    }

    if (existingRun && existingRun.runKey) {
      // Valid run exists: open drawer, do not directly invoke /agent/flow/execute again
      setIsRunPanelOpen(true);
      return;
    }

    // No existing run: directly invoke the execute API
    await executeFlowRun();
  };

  // Streams the run's reasoning logs. Deliberately keyed on interactionId alone:
  // drilling into a sub-flow re-renders this component without unmounting it, so
  // a run started in the parent keeps streaming while the user explores.
  useEffect(() => {
    if (!interactionId) return undefined;

    const base = environment.reasoningWsUrl || (environment.controlplaneUrl || '').replace(/^http/, 'ws');
    const ws = new WebSocket(`${base}/ws/reasoning-logs?interactionId=${interactionId}`);
    let timeoutId;
    let finalized = false;

    const finalize = (status) => {
      if (finalized) return;
      finalized = true;
      if (timeoutId) clearTimeout(timeoutId);
      setRunStatus(status === 'failure' ? 'failure' : 'success');
      setRunTimes((prev) => (prev ? { ...prev, endedAt: Date.now() } : prev));
      if (status === 'failure') setRunError('The run reported a failure. Review the steps above and try again.');
      if (status === 'timeout') {
        setRunError('The run is taking longer than expected and stopped reporting. Please try again.');
      }
      if (status === 'success') {
        if (activeSuppliedRef.current) {
          resolveSuppliedRun(activeSuppliedRef.current.referenceId);
        } else {
          fetchLatestBenchmarkRun();
        }
      }
      ws.close();
    };

    const resetTimeout = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => finalize('timeout'), REASONING_IDLE_TIMEOUT_MS);
    };

    ws.onopen = () => {
      setRunStatus('running');
      resetTimeout();
    };

    ws.onmessage = (event) => {
      resetTimeout();
      try {
        const data = JSON.parse(event.data);
        const { entries, mode, status } = extractReasoningEntries(data);
        if (entries.length) {
          setReasoningEntries((prev) => (mode === 'replace' ? entries : [...prev, ...entries]));
        }
        // Only a terminal status ends the run — long gaps between stages are
        // normal and the idle timeout above already covers a genuinely dead run.
        if (status === 'success' || status === 'failure') finalize(status);
      } catch {
        setReasoningEntries((prev) => [...prev, { reasoning: String(event.data), activityTemplateName: '', activityTemplateCode: '', branchIdentifier: '' }]);
      }
    };

    ws.onerror = (error) => console.error('Reasoning websocket error:', error);
    ws.onclose = () => { if (timeoutId) clearTimeout(timeoutId); };

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      ws.close();
    };
  }, [interactionId]);

  // Canvas run state, re-derived from the full entry list. Because it's derived
  // rather than accumulated, drilling into a sub-flow re-maps the same stream
  // onto the newly loaded canvas for free.
  const { nodeStates, branches } = useMemo(
    () => deriveRunState(reasoningEntries, activityNodeMap, { runStatus, selectedBranch }),
    [reasoningEntries, activityNodeMap, runStatus, selectedBranch]
  );

  const nodesWithRunState = useMemo(() => {
    if (nodeStates.size === 0 && !isRunning) return nodes;
    // runActive travels with every node so each one can tell "not reached yet"
    // (dim) from "no run happening" (render normally).
    return nodes.map((node) => ({
      ...node,
      data: { ...node.data, runState: nodeStates.get(node.id), runActive: isRunning },
    }));
  }, [nodes, nodeStates, isRunning]);

  // Decorate entries for the run panel: which canvas node each resolved to, and
  // whether it came from inside a sub-flow (so the panel can indent it).
  const decoratedEntries = useMemo(() => {
    const nameById = new Map(nodes.map((n) => [n.id, n.data?.activity_template_name || n.data?.activityTemplateName || '']));
    return reasoningEntries.map((entry, sourceIndex) => {
      const nodeId = resolveEntryNode(activityNodeMap, entry);
      const ownCode = nodes.find((n) => n.id === nodeId)?.data?.activity_template_code;
      return {
        ...entry,
        // Stable across branch filtering, so filtering doesn't replay the
        // panel's per-entry animation.
        sourceIndex,
        branchKey: entry.branchIdentifier || MAIN_BRANCH,
        nodeId,
        nodeName: nodeId ? nameById.get(nodeId) : '',
        // Rolled up = the entry's own activity isn't the canvas node itself, so
        // it executed inside that node's sub-flow.
        isRollup: Boolean(nodeId && ownCode && ownCode !== entry.activityTemplateCode),
      };
    });
  }, [reasoningEntries, activityNodeMap, nodes]);

  // A mapping failure is otherwise invisible: the panel streams normally while
  // the canvas stays blank, which reads as "the highlighting is weak" rather
  // than "nothing resolved". Warn once per run with what didn't match.
  const mapWarnedRef = useRef(false);
  useEffect(() => {
    if (mapWarnedRef.current || decoratedEntries.length === 0) return;
    if (decoratedEntries.some((e) => e.nodeId)) return;
    mapWarnedRef.current = true;
    console.warn(
      '[AgentFlowEditor] No reasoning step resolved to a canvas node — nodes will not highlight.',
      {
        sampleEntry: {
          activityTemplateCode: decoratedEntries[0].activityTemplateCode,
          activityTemplateName: decoratedEntries[0].activityTemplateName,
        },
        availableKeys: [...activityNodeMap.keys()],
      }
    );
  }, [decoratedEntries, activityNodeMap]);

  // The node to follow. During fan-out several nodes are legitimately running at
  // once, so don't scan nodeStates — the newest log line is where attention
  // belongs.
  const activeNodeId = useMemo(() => {
    for (let i = decoratedEntries.length - 1; i >= 0; i -= 1) {
      if (decoratedEntries[i].nodeId) return decoratedEntries[i].nodeId;
    }
    return null;
  }, [decoratedEntries]);

  const onMoveStart = useCallback((event) => {
    if (event) setUserMovedViewport(true);
  }, []);

  // Give the canvas the catalog's width back while a run streams.
  useEffect(() => {
    setCatalogCollapsed(isRunning);
  }, [isRunning]);

  // Empty string = enabled. While running the button stays live so it can
  // reopen the run panel.
  const runDisabledReason = useMemo(() => {
    if (isRunning) return '';
    if (!flowTemplateDetails?.flow_template_code) return 'Save this workflow before running it';
    return '';
  }, [isRunning, flowTemplateDetails?.flow_template_code]);

  const handleAddNode = (template) => {
    const activityType = template?.activity_type || template?.activityType || template?.activity_template_code;
    const activityCode = template?.activity_template_code || activityType;
    const activityName = template?.activity_template_name || template?.activityTemplateName;
    const activityDescription = template?.activity_template_description || template?.activityTemplateDescription;
    const newNode = {
      id: `${activityType}_${Date.now()}`,
      type: 'workflowNode',
      data: {
        id: `${activityType}_${Date.now()}`,
        activity_template_name: activityName,
        activity_type: activityType,
        activity_template_code: activityCode,
        activity_template_description: activityDescription,
        interaction_mode: template?.interaction_mode,
        scope: template?.scope,
      },
      position: { x: 100, y: 100 },
    };
    setNodes((nds) => [...nds, newNode]);
    setIsDirty(true);
    setOpenDialog(false);
  };

  const onCatalogDragStart = (event, template) => {
    event.dataTransfer.setData('application/reactflow-template', JSON.stringify(template));
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDropOnCanvas = (template, position) => {
    const activityType = template?.activity_type || template?.activity_template_code;
    const activityCode = template?.activity_template_code || activityType;
    const newNode = {
      id: `${activityType}_${Date.now()}`,
      type: 'workflowNode',
      data: {
        id: `${activityType}_${Date.now()}`,
        activity_template_name: template?.activity_template_name,
        activity_type: activityType,
        activity_template_code: activityCode,
        activity_template_description: template?.activity_template_description,
        interaction_mode: template?.interaction_mode,
        scope: template?.scope,
      },
      position,
    };
    setNodes((nds) => [...nds, newNode]);
    setIsDirty(true);
  };

  const onConnect = useCallback(async (params) => {
    setEdges((eds) => addEdge(params, eds));

    const newEdgeTemplate = {
      data: {
        source: params.source,
        target: params.target,
        type: 'precedes',
        properties: []
      }
    };

    setFlowTemplateDetails(prev => {
      if (!prev || !prev.configuration) return prev;
      const edges = prev.configuration.edges || [];
      if (edges.find(e => e.data?.source === params.source && e.data?.target === params.target)) return prev;
      return { ...prev, configuration: { ...prev.configuration, edges: [...edges, newEdgeTemplate] } };
    });

    try {
      const payload = {
        data: {
          flow_template_items: [
            {
              flow_template_code: flowTemplateDetails?.flow_template_code,
              configuration: {
                edges: [{
                  data: newEdgeTemplate.data
                }]
              }
            }
          ]
        }
      };
      await axios.post(
        `${environment.apiUrl}/agent/updateCustomFlowControl`,
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' } }
      );
    } catch (error) {
      if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) navigate('/login');
    }
  }, [setEdges, flowTemplateDetails?.flow_template_code, navigate]);

  const onNodeClick = (event, node) => {
    const linkedCode = node.data?.linked_flow_template_code;
    if (linkedCode && linkedCode !== '') {
      // Carry the campaign context through so Run stays available inside the
      // sub-flow (and the operator name keeps labelling the header).
      navigate('/AIStudio/customize', {
        state: {
          flow_template_code: linkedCode,
          operator_name: location.state?.operator_name,
          operator_id: location.state?.operator_id,
          fromOperator: location.state?.fromOperator,
          campaign_id: campaignId,
          project_id: projectId,
          returnTo: currentReturnTo(),
        }
      });
    } else {
      handleEditNode(node, true);
    }
  };

  const onNodeMouseEnter = useCallback((event, node) => {
    clearTimeout(hoverTimerRef.current);
    clearTimeout(hideTimerRef.current);
    // Capture the node's rect synchronously — event.currentTarget is nulled
    // by React before the setTimeout below would read it.
    const nodeEl = event.currentTarget;
    hoverTimerRef.current = setTimeout(() => {
      const anchorRect = nodeEl?.getBoundingClientRect?.();
      // The description now ships with the flow-template node data, so read it
      // directly instead of round-tripping to /manage/activity-templates.
      setHoverPreview({
        anchorRect,
        data: {
          activity_template_name: node.data?.activity_template_name || node.data?.activityTemplateName,
          activity_template_description: node.data?.activity_template_description,
        },
        nodeRef: node,
      });
    }, 300);
  }, []);

  const onNodeMouseLeave = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setHoverPreview(null);
    }, 250);
  }, []);

  // Position the hover preview in the best available free space around the node:
  // prefer the right side, flip to the left if it would overflow, then clamp
  // into the viewport vertically. Uses the preview's measured size.
  useLayoutEffect(() => {
    if (!hoverPreview?.anchorRect || !previewRef.current) return;
    const rect = hoverPreview.anchorRect;
    const { offsetWidth: pw, offsetHeight: ph } = previewRef.current;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const GAP = 14;
    let left;
    if (rect.right + GAP + pw <= vw) left = rect.right + GAP;            // room on the right
    else if (rect.left - GAP - pw >= 0) left = rect.left - GAP - pw;     // flip to the left
    else left = Math.max(8, Math.min(vw - pw - 8, rect.right + GAP));    // clamp into view
    const top = Math.max(8, Math.min(rect.top - 10, vh - ph - 8));
    setPreviewPos({ top, left });
  }, [hoverPreview]);

  const onEdgeClick = (event, edge) => {
    event.stopPropagation();
    setSelectedEdge(edge);
    handleEditEdge(edge, true);
  };

  const handleDeleteNode = (nodeId) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    setIsDirty(true);
    setMenuPosition(null);
  };

  const handleRemoveEdgeFromBackend = async (edgeSource, edgeTarget) => {
    setFlowTemplateDetails(prev => {
      if (!prev || !prev.configuration || !prev.configuration.edges) return prev;
      return {
        ...prev,
        configuration: {
          ...prev.configuration,
          edges: prev.configuration.edges.filter(e => !(e.data?.source === edgeSource && e.data?.target === edgeTarget))
        }
      };
    });

    try {
      const payload = {
        data: {
          flow_template_items: [
            {
              flow_template_code: flowTemplateDetails?.flow_template_code,
              configuration: {
                edges: [{
                  data: {
                    source: edgeSource,
                    target: edgeTarget,
                    status: 'deleted'
                  }
                }]
              }
            }
          ]
        }
      };
      await axios.post(
        `${environment.apiUrl}/agent/updateCustomFlowControl`,
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' } }
      );
    } catch (error) {
      if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) navigate('/login');
    }
  };

  const handleDeleteEdge = async (edge) => {
    setEdges(eds => eds.filter(e => e.id !== edge.id));
    setEdgeMenuPosition(null);
    await handleRemoveEdgeFromBackend(edge.source, edge.target);
  };

  const onEdgesDelete = async (edgesToDelete) => {
    // Edge deletes persist immediately, so block them while a run is in flight.
    if (isRunning) return;
    for (const edge of edgesToDelete) {
      await handleRemoveEdgeFromBackend(edge.source, edge.target);
    }
  };

  const handleEditEdge = (edge, readOnly = false) => {
    const templateEdge = flowTemplateDetails?.configuration?.edges?.find(e => e.data?.source === edge.source && e.data?.target === edge.target);
    const properties = templateEdge?.data?.properties || [];
    setIsEdgeDrawerReadOnly(readOnly);
    setEditEdgeDetails({
      source: edge.source,
      target: edge.target,
      properties: JSON.parse(JSON.stringify(properties))
    });
    
    setIsEdgeEditDrawerOpen(true);
  };

  const handleEditEdgePropertyChange = (propertyKey, newValue) => {
    setEditEdgeDetails((prev) => {
      if (!prev) return prev;
      const updatedProps = prev.properties.map(prop => {
        if ((prop.key || prop.name) === propertyKey) {
          return { ...prop, value: newValue };
        }
        return prop;
      });
      return { ...prev, properties: updatedProps };
    });
  };

  const handleSaveEditedEdge = async () => {
    try {
      const originalEdge = flowTemplateDetails?.configuration?.edges?.find(
        e => e.data?.source === editEdgeDetails.source && e.data?.target === editEdgeDetails.target
      );
      
      const payload = {
        data: {
          flow_template_items: [
            {
              flow_template_code: flowTemplateDetails?.flow_template_code,
              configuration: {
                edges: [
                  {
                    data: {
                      ...originalEdge?.data,
                      source: editEdgeDetails.source,
                      target: editEdgeDetails.target,
                      properties: editEdgeDetails.properties
                    }
                  }
                ]
              }
            }
          ]
        }
      };

      await axios.post(
        `${environment.apiUrl}/agent/updateCustomFlowControl`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setFlowTemplateDetails(prev => {
        if (!prev || !prev.configuration || !prev.configuration.edges) return prev;
        
        const updatedEdges = prev.configuration.edges.map(e => {
          if (e.data?.source === editEdgeDetails.source && e.data?.target === editEdgeDetails.target) {
            return {
              ...e,
              data: {
                ...e.data,
                properties: editEdgeDetails.properties
              }
            };
          }
          return e;
        });
        
        return {
          ...prev,
          configuration: {
            ...prev.configuration,
            edges: updatedEdges
          }
        };
      });
      setIsEdgeEditDrawerOpen(false);
    } catch (error) {
      if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) navigate('/login');
    }
  };

  const handleEditNode = async (node, readOnly = false) => {
    try {
      setMenuPosition(null);
      setIsDrawerReadOnly(readOnly);
      let activityCode = node.data?.activity_template_code || node.id || null;
      // If the code has a timestamp suffix (long number at the end), remove only the suffix.
      if (activityCode && activityCode.includes('_') && /_\d{10,}$/.test(activityCode)) {
        activityCode = activityCode.substring(0, activityCode.lastIndexOf('_'));
      }
      const res = await axios.get(
        `${environment.controlplaneUrl}/manage/activity-templates/${encodeURIComponent(activityCode)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const responseData = res.data?.activity_template || res.data?.data || res.data;
      const config = responseData?.configuration || responseData;
      setEditNodeDetails({
        id: responseData?.id || null,
        activity_type: responseData?.activity_type || node.data?.activity_type || '',
        activity_template_name: responseData?.activity_template_name || '',
        activity_template_description: responseData?.activity_template_description || '',
        activity_template_code: activityCode,
        primary_input: config?.primary_input || [],
        primary_output: config?.primary_output || null,
        // Data source binding — the same response already carries these, so the
        // drawer surfaces the binding without an extra round trip. Sub-flow
        // nodes never show it, even via the context-menu edit path.
        has_integration_binding:
          responseData?.has_integration_binding === true && !node.data?.linked_flow_template_code,
        integration_binding_id: responseData?.integration_binding_id || null,
        integration_alias: responseData?.integration_alias || null,
        integration_object: responseData?.object || null,
      });
      setEditNodeConfig(config?.task_configuration || config?.taskConfiguration || config?.primaryInput || config?.task_configuration_items || []);
      setIsDescExpanded(false);
      setIsIntegrationDrawerOpen(false);
      setIsEditDrawerOpen(true);
    } catch (error) {
      if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) navigate('/login');
    }
  };

  const handleEditNodeConfigChange = (key, value) =>
    setEditNodeConfig((prev) =>
      (prev || []).map((item) => (item.key === key ? { ...item, value } : item))
    );

  const handleEditNodeCellChange = (key, rowIndex, colKey, value) =>
    setEditNodeConfig((prev) =>
      (prev || []).map((item) =>
        item.key !== key || !Array.isArray(item.value)
          ? item
          : {
              ...item,
              value: item.value.map((row, i) =>
                i === rowIndex ? { ...row, [colKey]: value } : row
              ),
            }
      )
    );

  // Soft delete/restore. The element or row stays in the array with active:false
  // so it round-trips into the PUT payload (task_configuration is sent verbatim)
  // and stays restorable.
  const handleEditNodeFieldActiveChange = (key, active) =>
    setEditNodeConfig((prev) =>
      (prev || []).map((item) => (item.key === key ? { ...item, active } : item))
    );

  const handleEditNodeRowActiveChange = (key, rowIndex, active) =>
    setEditNodeConfig((prev) =>
      (prev || []).map((item) =>
        item.key !== key || !Array.isArray(item.value)
          ? item
          : {
              ...item,
              value: item.value.map((row, i) => (i === rowIndex ? { ...row, active } : row)),
            }
      )
    );

  const handleSaveEditedNode = async () => {
    try {
      const activityCode = editNodeDetails?.activity_template_code;
      const payload = {
        activity_template_name: editNodeDetails?.activity_template_name || '',
        activity_template_description: editNodeDetails?.activity_template_description || '',
        configuration: {
          primary_input: editNodeDetails?.primary_input || [],
          primary_output: editNodeDetails?.primary_output || null,
          task_configuration: editNodeConfig,
        },
      };
      await axios.put(
        `${environment.controlplaneUrl}/manage/activity-templates/${encodeURIComponent(activityCode)}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setIsEditDrawerOpen(false);
    } catch (error) {
      if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) navigate('/login');
    }
  };

  const filteredTemplates = activitytemplates?.filter(template => {
    const name = (template.activity_template_name || template.activityTemplateName || '').toLowerCase();
    const description = (template.activity_template_description || template.activityTemplateDescription || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return name.includes(term) || description.includes(term);
  });

  const groupedTemplates = filteredTemplates?.reduce((acc, template) => {
    const rawType = template.task_type || template.taskType || template.activity_type || template.activityType || 'Uncategorized';
    const taskType = toTitleCase(rawType);
    if (!acc[taskType]) acc[taskType] = [];
    acc[taskType].push(template);
    return acc;
  }, {});

  return (
    <Box sx={{ backgroundColor: dsVar('page'), height: '100vh', color: dsVar('fg-body'), display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 24px 16px', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <IconButton onClick={handleBack} aria-label="Back" sx={{ color: ink('#159DFF') }}><KeyboardBackspaceIcon /></IconButton>
          <Typography sx={{ color: dsVar('accent-fg'), fontWeight: 600, fontSize: '18px' }}>Customize Agentic Workflow</Typography>
          <TextField
            size="small"
            value={text1}
            label="Give your agentic workflow a name"
            variant="standard"
            sx={{
              width: '300px',
              '& .MuiInput-root': { color: dsVar('fg-body'), fontSize: '18px' },
              '& .MuiInputLabel-root': { color: dsVar('fg-2') }
            }}
            onChange={(e) => { setText1(e.target.value); setIsDirty(true); }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: '16px' }}>
          {/* When this workflow last succeeded: the campaign's own runs when
              opened from one, otherwise every run of the flow. */}
          {(campaignId || location.state?.flow_template_code) && (
            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '10px', maxWidth: 360 }}>
              <LastSuccessfulRun campaignId={campaignId} flowCode={location.state?.flow_template_code} />
            </Box>
          )}
          <Tooltip title={runDisabledReason || (isRunning ? 'Show run progress' : '')}>
            {/* span wrapper: MUI tooltips need a non-disabled child to fire */}
            <span>
              <Button
                onClick={handleTestClick}
                disabled={Boolean(runDisabledReason) || isSavingBeforeRun || isCheckingRun}
                startIcon={
                  isRunning || isSavingBeforeRun || isCheckingRun
                    ? <CircularProgress size={16} thickness={5} sx={{ color: dsVar('accent-fg') }} />
                    : <PlayArrowIcon />
                }
                sx={{
                  marginTop: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '4px',
                  padding: '6px 16px',
                  color: dsVar('accent-fg'),
                  border: `1px solid ${dsVar('accent-fg')}`,
                  backgroundColor: 'transparent',
                  '&:hover': { backgroundColor: 'rgba(255, 222, 89, 0.12)', border: `1px solid ${dsVar('accent-fg')}` },
                  '&.Mui-disabled': { color: dsVar('fg-muted'), border: `1px solid ${dsVar('line')}` },
                }}
              >
                {isSavingBeforeRun ? 'Saving…' : isCheckingRun ? 'Checking…' : isRunning ? 'Running…' : 'Backtest'}
              </Button>
            </span>
          </Tooltip>
          <ButtonK text="Save" color="#131F32" backgroundColor="#FFD600" handleClick={updateFlowTemplateDetails} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', borderTop: `1px solid ${dsVar('line')}`, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* ── Left Panel ── */}
        <Box sx={{
          width: catalogCollapsed ? 0 : '28%',
          flexShrink: 0,
          overflow: 'hidden',
          transition: 'width 0.25s ease',
          // A 1px border on a zero-width element still draws a stray line.
          borderRight: catalogCollapsed ? 'none' : `1px solid ${dsVar('line')}`,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: dsVar('surface'),
        }}>

          {/* Toggle header */}
          <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: `1px solid ${dsVar('line')}` }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: dsVar('fg-muted'), textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>Agent Catalog</Typography>
            <Box sx={{ display: 'flex', gap: '4px', backgroundColor: dsVar('page'), borderRadius: '10px', p: '4px' }}>
              <Box
                onClick={() => setCatalogView('assistant')}
                sx={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  py: '6px', borderRadius: '7px', cursor: 'pointer', transition: 'all 0.2s ease',
                  backgroundColor: catalogView === 'assistant' ? dsVar('raised') : 'transparent',
                  border: catalogView === 'assistant' ? `1px solid ${dsVar('accent-fg')}` : '1px solid transparent',
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 13, color: catalogView === 'assistant' ? dsVar('accent-fg') : dsVar('fg-muted') }} />
                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: catalogView === 'assistant' ? dsVar('accent-fg') : dsVar('fg-muted') }}>Assistant</Typography>
              </Box>
              <Box
                onClick={() => setCatalogView('list')}
                sx={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  py: '6px', borderRadius: '7px', cursor: 'pointer', transition: 'all 0.2s ease',
                  backgroundColor: catalogView === 'list' ? dsVar('raised') : 'transparent',
                  border: catalogView === 'list' ? `1px solid ${dsVar('line')}` : '1px solid transparent',
                }}
              >
                <ViewListIcon sx={{ fontSize: 13, color: catalogView === 'list' ? dsVar('fg-body') : dsVar('fg-muted') }} />
                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: catalogView === 'list' ? dsVar('fg-body') : dsVar('fg-muted') }}>Catalog</Typography>
              </Box>
            </Box>
          </Box>

          {/* ── View: Agentic Workflow Architect ── */}
          <Box sx={{ flex: 1, display: catalogView === 'assistant' ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden' }}>
            <ChatAssistance
              agentCode="QUERY_AGENT_ORCHESTRATION_AND_RESPOND"
              title="Agentic Workflow Architect"
              subtitle="AI Powered • Online"
              initialMessage="Hi! I'm your Agentic Workflow Architect. Describe what you want to build and I'll help you design the agent flow — choosing the right agents, structuring the sequence, and connecting the steps."
            />
          </Box>

          {/* ── View: Agent Catalog List ── */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: catalogView === 'list' ? 'block' : 'none' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search agents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  marginBottom: '12px',
                  '& .MuiOutlinedInput-root': {
                    color: dsVar('fg-body'),
                    backgroundColor: dsVar('page'),
                    '& fieldset': { borderColor: dsVar('line') },
                    '&:hover fieldset': { borderColor: dsVar('accent-fg') },
                    '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') },
                  },
                  '& .MuiInputBase-input::placeholder': { color: dsVar('fg-2'), opacity: 1 }
                }}
              />
              <ButtonK text="+ Create an Agent" color="#131F32" backgroundColor="#FFD600" sx={{ width: '100%', marginBottom: '12px' }} handleClick={() => setOpenDialog(true)} />
              {Object.entries(groupedTemplates || {}).map(([taskType, templates]) => (
                <Accordion key={taskType} sx={{ backgroundColor: dsVar('surface'), color: dsVar('fg-body'), border: `1px solid ${dsVar('line')}`, marginBottom: '8px' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: dsVar('fg-2') }} />}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{taskType} ({templates.length})</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ overflow: 'visible' }}>
                    {templates.map((t, i) => (
                      <Card
                        key={i}
                        draggable
                        onDragStart={(e) => onCatalogDragStart(e, t)}
                        sx={{
                          backgroundColor: dsVar('page'),
                          borderRadius: '16px',
                          border: `1px solid ${dsVar('line')}`,
                          marginBottom: '8px',
                          position: 'relative',
                          cursor: 'grab',
                          transition: 'all 0.2s ease',
                          overflow: 'visible',
                          '&:hover': {
                            borderColor: '#159DFF',
                            boxShadow: '0 0 10px rgba(21, 157, 255, 0.15)'
                          }
                        }}>
                        <CardContent sx={{ padding: '12px', position: 'relative', display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '8px' }}>
                          <ModeLabelChip mode={t.interaction_mode} />
                          <ScopeBadge scope={t.scope} />
                          <Box sx={{ color: ink('#159DFF'), flexShrink: 0, display: 'flex', alignItems: 'center', mt: '2px', mr: '2px' }}>
                            <SmartToyIcon sx={{ fontSize: '18px' }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: dsVar('fg-body'), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {t.activity_template_name || t.activityTemplateName || 'Unnamed Task'}
                            </Typography>
                            {t.org_id && (
                              <Chip
                                label="CUSTOM"
                                size="small"
                                sx={{
                                  height: '16px', fontSize: '9px', fontWeight: 700, letterSpacing: '0.05em',
                                  mt: '4px', mb: '2px',
                                  backgroundColor: 'rgba(167,139,250,0.08)',
                                  color: ink('#A78BFA'),
                                  border: '1px solid rgba(167,139,250,0.25)',
                                  borderRadius: '4px',
                                  '& .MuiChip-label': { px: '6px' },
                                }}
                              />
                            )}
                            <Typography sx={{ fontSize: '11px', color: dsVar('fg-2'), marginTop: '4px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {t.activity_template_description || t.activityTemplateDescription || ''}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
        </Box>
        <Box sx={{
          flex: 1,
          minWidth: 0,
          position: 'relative',
          backgroundColor: dsVar('page'),
          '& .react-flow__panel.react-flow__minimap': {
            margin: '0px !important',
            top: 'auto !important',
            left: 'auto !important',
            right: '0px !important',
            bottom: '0px !important',
            borderBottomRightRadius: '0 !important',
            borderTopRightRadius: '0 !important',
            borderBottomLeftRadius: '0 !important',
            position: 'absolute !important',
            transform: 'none !important',
          }
        }}>
          <style>{nodeStyles}</style>
          <ReactFlowProvider>
            <FlowDropTarget onDrop={handleDropOnCanvas}>
            <ReactFlow
              nodes={nodesWithRunState}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onNodeMouseEnter={onNodeMouseEnter}
              onNodeMouseLeave={onNodeMouseLeave}
              onEdgeClick={onEdgeClick}
              onEdgesDelete={onEdgesDelete}
              onMoveStart={onMoveStart}
              nodeTypes={nodeTypes}
              connectionLineStyle={connectionLineStyle}
              defaultEdgeOptions={edgeOptions}
              fitView
            >
              <FitViewOnFlowChange flowKey={flowLoadSeq} />
              <FocusRunningNode nodeId={activeNodeId} enabled={isRunning && !userMovedViewport} />
              <RefitOnLayoutChange
                trigger={`${isRunPanelOpen}|${catalogCollapsed}`}
                skip={isRunning && !userMovedViewport}
              />
              <Controls />
              <MiniMap
                className="custom-minimap"
                position="bottom-right"
                ariaLabel="Agent Flow Mini Map"
                nodeColor={(node) => {
                  const isSubFlow = node.data?.linked_flow_template_code && node.data.linked_flow_template_code !== '';
                  if (isSubFlow) return '#7C3AED';
                  const c = taskTypeConfig[node.data?.activity_type] || taskTypeConfig[node.data?.activityType];
                  return c?.color || '#607D8B';
                }}
                maskColor={dsVar('page', 0.85)}
                style={{ backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, borderRadius: '8px', margin: 0, right: 0, bottom: 0 }}
                title="Agent Flow Mini Map"
              />
              <Background color={dsVar('line')} variant="dots" gap={20} size={1} />
            </ReactFlow>
            </FlowDropTarget>
          </ReactFlowProvider>
          {/* Manual override for the run's auto-collapse, so a user who wants the
              catalog mid-run isn't stuck without it. */}
          <Tooltip title={catalogCollapsed ? 'Show catalog' : 'Hide catalog'} placement="right">
            <IconButton
              onClick={() => setCatalogCollapsed((c) => !c)}
              sx={{
                position: 'absolute',
                top: '50%',
                left: 0,
                transform: 'translateY(-50%)',
                zIndex: 5,
                width: 18,
                height: 44,
                borderRadius: '0 8px 8px 0',
                backgroundColor: dsVar('surface'),
                border: `1px solid ${dsVar('line')}`,
                borderLeft: 'none',
                color: dsVar('fg-muted'),
                '&:hover': { backgroundColor: dsVar('raised'), color: dsVar('accent-fg') },
              }}
            >
              {catalogCollapsed ? <ChevronRightIcon sx={{ fontSize: 16 }} /> : <ChevronLeftIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          {/* Run status chip — always accurate, unlike per-node state which
              depends on activity codes resolving to something on this canvas. */}
          {isRunning && (
            <Box
              onClick={() => setIsRunPanelOpen(true)}
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: '999px',
                cursor: 'pointer',
                backgroundColor: dsVar('surface', 0.92),
                border: '1px solid rgba(255, 222, 89, 0.4)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              }}
            >
              <Box className="agent-dot-pulse" sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: dsVar('accent') }} />
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: dsVar('accent-fg'), letterSpacing: '0.04em' }}>
                Run in progress
              </Typography>
            </Box>
          )}
          {/* Hover preview popover */}
          {hoverPreview && (
            <Box
              ref={previewRef}
              onMouseEnter={() => clearTimeout(hideTimerRef.current)}
              onMouseLeave={onNodeMouseLeave}
              sx={{
                position: 'fixed',
                zIndex: 1500,
                top: previewPos ? previewPos.top : (hoverPreview.anchorRect?.top || 200) - 10,
                left: previewPos ? previewPos.left : (hoverPreview.anchorRect?.right || 400) + 14,
                visibility: previewPos ? 'visible' : 'hidden',
                maxWidth: 320,
                minWidth: 220,
                backgroundColor: dsVar('raised'),
                border: `1px solid ${dsVar('line')}`,
                borderRadius: '12px',
                padding: '16px',
                boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${dsVar('fg', 0.04)}`,
                backdropFilter: 'blur(8px)',
                animation: 'fadeInPreview 0.15s ease-out',
                '@keyframes fadeInPreview': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '13px', color: dsVar('fg'), mb: 0.75, fontFamily: "'JetBrains Mono', monospace" }}>
                {hoverPreview.data?.activity_template_name || 'Agent'}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: dsVar('fg-2'), lineHeight: 1.6 }}>
                {(hoverPreview.data?.activity_template_description || '').length > 150
                  ? `${hoverPreview.data.activity_template_description.substring(0, 150)}\u2026`
                  : hoverPreview.data?.activity_template_description || 'No description available.'}
              </Typography>
              <Typography
                onClick={() => { setHoverPreview(null); handleEditNode(hoverPreview.nodeRef, true); }}
                sx={{
                  fontSize: '11px',
                  color: dsVar('accent-fg'),
                  mt: 1.5,
                  cursor: 'pointer',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  '&:hover': { textDecoration: 'underline', color: ink('#FFE87A') },
                  transition: 'color 0.15s ease',
                }}
              >
                View details &rarr;
              </Typography>
            </Box>
          )}
        </Box>

        {/* Inline run panel: a flex sibling of the canvas rather than an overlay,
            so the canvas and its live node highlighting stay visible. */}
        <FlowRunPanel
          open={isRunPanelOpen}
          onClose={() => setIsRunPanelOpen(false)}
          status={runStatus}
          entries={decoratedEntries}
          branches={branches}
          selectedBranch={selectedBranch}
          onSelectBranch={setSelectedBranch}
          branchLabelFor={branchLabel}
          error={runError}
          onDismissError={() => setRunError(null)}
          times={runTimes}
          operatorName={text1}
          latestRun={latestBenchmarkRun}
          isCheckingRun={isCheckingRun}
          onReRunTest={executeFlowRun}
          benchmarkFlowCode={flowTemplateDetails?.flow_template_code ? benchmarkFlowCode(flowTemplateDetails.flow_template_code) : ''}
          // A run key from the supplied list opens that run; with none, a
          // supplied sample that just finished opens its own results.
          onViewResults={(runKey) => handleViewBenchmarkResults(runKey)}
          viewResultsLabel={activeSuppliedRef.current ? 'View your sample\'s results' : undefined}
          suppliedNotScored={suppliedNotScored}
          onResubmitSample={flowTemplateDetails?.flow_template_code ? reopenSampleWizard : undefined}
          suppliedFamilies={suppliedSampleFamilies(flowTemplateDetails?.flow_template_code || '')}
          projectId={projectId || location.state?.project_id || 'benchmark'}
          suppliedRunsVersion={suppliedRunsVersion}
          onBenchmarkSample={flowTemplateDetails?.flow_template_code ? () => setIsSampleWizardOpen(true) : undefined}
        />

        <SuppliedSampleWizard
          open={isSampleWizardOpen}
          onClose={() => setIsSampleWizardOpen(false)}
          workflowCode={flowTemplateDetails?.flow_template_code || ''}
          projectId={projectId || location.state?.project_id || 'benchmark'}
          onRunStarted={handleSuppliedRunStarted}
        />
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ backgroundColor: dsVar('page'), color: dsVar('accent-fg') }}>Create an AI Task</DialogTitle>
        <DialogContent sx={{ backgroundColor: dsVar('page') }}>
          <AdminActivityTemplates />
        </DialogContent>
      </Dialog>

      {/* Node context menu removed — drawer opens directly on node click */}

      {/* Edge context menu removed — drawer opens directly on edge click */}

      <Drawer anchor="right" open={isEditDrawerOpen} onClose={() => setIsEditDrawerOpen(false)} PaperProps={{ sx: { width: '500px', backgroundColor: dsVar('surface'), color: dsVar('fg-body') } }}>
        <Box sx={{ padding: '24px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: dsVar('accent-fg'), fontWeight: 600 }}>Edit Agent Parameters</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                title={isDrawerReadOnly ? 'Switch to Edit mode' : 'Switch to Read-only mode'}
                onClick={() => setIsDrawerReadOnly(r => !r)}
                sx={{
                  color: isDrawerReadOnly ? dsVar('fg-2') : dsVar('accent-fg'),
                  backgroundColor: isDrawerReadOnly ? dsVar('raised') : 'rgba(255,222,89,0.1)',
                  border: `1px solid ${isDrawerReadOnly ? dsVar('line') : dsVar('accent-fg')}`,
                  borderRadius: '8px',
                  px: 1.5,
                  gap: 0.5,
                  '&:hover': { backgroundColor: 'rgba(255,222,89,0.15)' }
                }}
              >
                {isDrawerReadOnly ? <LockIcon sx={{ fontSize: 16 }} /> : <LockOpenIcon sx={{ fontSize: 16 }} />}
                <Typography sx={{ fontSize: '12px', fontWeight: 600, ml: 0.5 }}>
                  {isDrawerReadOnly ? 'Read-only' : 'Editing'}
                </Typography>
              </IconButton>
              <IconButton onClick={() => setIsEditDrawerOpen(false)} sx={{ color: dsVar('fg-2') }}><CloseIcon /></IconButton>
            </Box>
          </Box>
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ color: dsVar('fg-2'), fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em', mb: 1 }}>Task Name</Typography>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              disabled={isDrawerReadOnly}
              value={editNodeDetails?.activity_template_name || ''}
              onChange={(e) => setEditNodeDetails({ ...editNodeDetails, activity_template_name: e.target.value })}
              sx={{
                '& .MuiInputBase-root': { color: dsVar('fg-body'), fontSize: '15px', backgroundColor: dsVar('surface') },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('line') },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' }
              }}
            />
            <Box sx={{ mt: 2.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ color: dsVar('fg-2'), fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em' }}>Task Description</Typography>
              {(editNodeDetails?.activity_template_description || '').length > 120 && (
                <Box
                  onClick={() => setIsDescExpanded((v) => !v)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.25, cursor: 'pointer', color: ink('#159DFF'), fontSize: '12px', fontWeight: 600, userSelect: 'none' }}
                >
                  {isDescExpanded ? 'Show less' : 'Show more'}
                  <ExpandMoreIcon sx={{ fontSize: 16, transform: isDescExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                </Box>
              )}
            </Box>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              maxRows={isDescExpanded ? 20 : 2}
              variant="outlined"
              disabled={isDrawerReadOnly}
              value={editNodeDetails?.activity_template_description || ''}
              onChange={(e) => setEditNodeDetails({ ...editNodeDetails, activity_template_description: e.target.value })}
              sx={{
                '& .MuiInputBase-root': { color: dsVar('fg-body'), fontSize: '14px', backgroundColor: dsVar('surface') },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('line') },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' }
              }}
            />
          </Box>

          {editNodeDetails?.has_integration_binding && editNodeDetails?.integration_alias && (
            <IntegrationBindingCard
              alias={editNodeDetails.integration_alias}
              accent="#FFDE59"
              onOpen={() => setIsIntegrationDrawerOpen(true)}
            />
          )}

          <TaskConfigurationForm
            key={editNodeDetails?.activity_template_code || 'node-config'}
            config={editNodeConfig}
            readOnly={isDrawerReadOnly}
            accent="#FFDE59"
            onFieldChange={handleEditNodeConfigChange}
            onCellChange={handleEditNodeCellChange}
            onFieldActiveChange={handleEditNodeFieldActiveChange}
            onRowActiveChange={handleEditNodeRowActiveChange}
          />
          <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${dsVar('line')}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Hidden mid-run so the template can't be mutated while executing. */}
            {selectedNode && !isRunning && (
              <IconButton
                size="small"
                title="Delete node"
                onClick={() => { handleDeleteNode(selectedNode.id); setIsEditDrawerOpen(false); }}
                sx={{ color: ink('#FF4D4D'), border: '1px solid #FF4D4D', borderRadius: '8px', px: 1.5, '&:hover': { backgroundColor: 'rgba(255,77,77,0.1)' } }}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 600, ml: 0.5 }}>Delete</Typography>
              </IconButton>
            )}
            {!isDrawerReadOnly && (
              <ButtonK text="Save" backgroundColor="#FFDE59" color="#131F32" sx={{ fontWeight: 600, px: 4 }} handleClick={handleSaveEditedNode} />
            )}
          </Box>
        </Box>
      </Drawer>

      <Drawer anchor="right" open={isEdgeEditDrawerOpen} onClose={() => setIsEdgeEditDrawerOpen(false)} PaperProps={{ sx: { width: '500px', backgroundColor: dsVar('surface'), color: dsVar('fg-body') } }}>
        <Box sx={{ padding: '24px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: dsVar('accent-fg'), fontWeight: 600 }}>Control Flow Parameters</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                title={isEdgeDrawerReadOnly ? 'Switch to Edit mode' : 'Switch to Read-only mode'}
                onClick={() => setIsEdgeDrawerReadOnly(r => !r)}
                sx={{
                  color: isEdgeDrawerReadOnly ? dsVar('fg-2') : dsVar('accent-fg'),
                  backgroundColor: isEdgeDrawerReadOnly ? dsVar('raised') : 'rgba(255,222,89,0.1)',
                  border: `1px solid ${isEdgeDrawerReadOnly ? dsVar('line') : dsVar('accent-fg')}`,
                  borderRadius: '8px',
                  px: 1.5,
                  gap: 0.5,
                  '&:hover': { backgroundColor: 'rgba(255,222,89,0.15)' }
                }}
              >
                {isEdgeDrawerReadOnly ? <LockIcon sx={{ fontSize: 16 }} /> : <LockOpenIcon sx={{ fontSize: 16 }} />}
                <Typography sx={{ fontSize: '12px', fontWeight: 600, ml: 0.5 }}>
                  {isEdgeDrawerReadOnly ? 'Read-only' : 'Editing'}
                </Typography>
              </IconButton>
              <IconButton onClick={() => setIsEdgeEditDrawerOpen(false)} sx={{ color: dsVar('fg-2') }}><CloseIcon /></IconButton>
            </Box>
          </Box>
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ color: dsVar('fg-2'), fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif', mb: 1 }}>Source Node</Typography>
            <Typography sx={{ color: dsVar('fg-body'), fontSize: '14px', mb: 2 }}>
              {nodes.find(n => n.id === editEdgeDetails?.source)?.data?.activity_template_name || editEdgeDetails?.source || 'N/A'}
            </Typography>
            
            <Typography sx={{ color: dsVar('fg-2'), fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif', mb: 1 }}>Target Node</Typography>
            <Typography sx={{ color: dsVar('fg-body'), fontSize: '14px', mb: 2 }}>
              {nodes.find(n => n.id === editEdgeDetails?.target)?.data?.activity_template_name || editEdgeDetails?.target || 'N/A'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {editEdgeDetails?.properties?.length === 0 && (
              <Typography sx={{ color: dsVar('fg-2'), fontSize: '14px', fontStyle: 'italic' }}>No configurable parameters for this edge.</Typography>
            )}
            {editEdgeDetails?.properties?.map((prop, idx) => {
              const { type, label, info, key, name, value, options } = prop;
              const propKey = key || name || String(idx);
              const propLabel = label || propKey;
              
              if (type === 'MULTITEXT') {
                return (
                  <Box key={propKey} sx={{ mb: 3 }}>
                    <Typography sx={{ color: dsVar('fg-2'), fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em', mb: 1.5 }}>{propLabel}</Typography>
                    <Box sx={{
                      '& .MuiInputBase-root': { color: dsVar('fg-body'), backgroundColor: dsVar('surface'), borderRadius: '8px', fontSize: '14px' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('line') },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
                      '& .MuiChip-root': { backgroundColor: dsVar('raised'), color: dsVar('fg-body'), border: `1px solid ${dsVar('line')}` },
                      '& .MuiChip-deleteIcon': { color: dsVar('fg-2'), '&:hover': { color: ink('#FF4D4D') } },
                      '& .MuiTypography-root': { display: 'none' }, 
                    }}>
                      <ChipInput
                        question=""
                        minRows={3}
                        placeholder={info || propLabel}
                        values={value || []}
                        onChipChange={(chips) => handleEditEdgePropertyChange(propKey, chips)}
                      />
                    </Box>
                  </Box>
                );
              } else if (type === 'TEXTINPUT') {
                if (options && options.length > 0) {
                  const currentValueObj = typeof value === 'object' && value !== null ? (value.id !== undefined ? value.id : value.value !== undefined ? value.value : JSON.stringify(value)) : (value || '');
                  return (
                    <Box key={propKey} sx={{ mb: 3 }}>
                      <Typography sx={{ color: dsVar('fg-2'), fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em', mb: 1.5 }}>{propLabel}</Typography>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        variant="outlined"
                        disabled={isEdgeDrawerReadOnly}
                        value={currentValueObj}
                        onChange={(e) => {
                          const selectedPrimitive = e.target.value;
                          const selectedOpt = options.find(opt => {
                            const isObj = typeof opt === 'object' && opt !== null;
                            const optVal = isObj ? (opt.id !== undefined ? opt.id : opt.value !== undefined ? opt.value : JSON.stringify(opt)) : opt;
                            return optVal === selectedPrimitive;
                          });
                          handleEditEdgePropertyChange(propKey, selectedOpt !== undefined ? selectedOpt : selectedPrimitive);
                        }}
                        sx={{
                          '& .MuiInputBase-root': { color: dsVar('fg-body'), fontSize: '14px', backgroundColor: dsVar('surface') },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('line') },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
                          '& .MuiSvgIcon-root': { color: dsVar('fg-2') },
                          textAlign: 'left'
                        }}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              sx: { backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, color: dsVar('fg-body') }
                            }
                          }
                        }}
                      >
                         {options.map((opt, optIdx) => {
                           const isObj = typeof opt === 'object' && opt !== null;
                           const optValue = isObj ? (opt.id !== undefined ? opt.id : opt.value !== undefined ? opt.value : JSON.stringify(opt)) : opt;
                           const optLabel = isObj ? (opt.label !== undefined ? opt.label : opt.name !== undefined ? opt.name : JSON.stringify(opt)) : opt;
                           return (
                             <MenuItem key={optIdx} value={optValue} sx={{ color: dsVar('fg-body'), '&:hover': { backgroundColor: dsVar('raised') }, '&.Mui-selected': { backgroundColor: dsVar('line') } }}>{optLabel}</MenuItem>
                           );
                         })}
                      </TextField>
                    </Box>
                  );
                } else {
                  const currentValuePlain = typeof value === 'object' && value !== null ? (value.id !== undefined ? value.id : value.value !== undefined ? value.value : JSON.stringify(value)) : (value || '');
                  return (
                    <Box key={propKey} sx={{ mb: 3 }}>
                      <Typography sx={{ color: dsVar('fg-2'), fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em', mb: 1.5 }}>{propLabel}</Typography>
                      <TextField
                        size="small"
                        fullWidth
                        variant="outlined"
                        disabled={isEdgeDrawerReadOnly}
                        value={currentValuePlain}
                        onChange={(e) => handleEditEdgePropertyChange(propKey, e.target.value)}
                        sx={{
                          '& .MuiInputBase-root': { color: dsVar('fg-body'), fontSize: '14px', backgroundColor: dsVar('surface') },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('line') },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' }
                        }}
                      />
                    </Box>
                  );
                }
              }
              return null;
            })}
          </Box>
          <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${dsVar('line')}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Hidden mid-run so the template can't be mutated while executing. */}
            {selectedEdge && !isRunning && (
              <IconButton
                size="small"
                title="Delete edge"
                onClick={() => { handleDeleteEdge(selectedEdge); setIsEdgeEditDrawerOpen(false); }}
                sx={{ color: ink('#FF4D4D'), border: '1px solid #FF4D4D', borderRadius: '8px', px: 1.5, '&:hover': { backgroundColor: 'rgba(255,77,77,0.1)' } }}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 600, ml: 0.5 }}>Delete</Typography>
              </IconButton>
            )}
            {!isEdgeDrawerReadOnly && (
              <ButtonK text="Save" backgroundColor="#FFDE59" color="#131F32" sx={{ fontWeight: 600, px: 4 }} handleClick={handleSaveEditedEdge} />
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Mounted last so it layers over the node drawer, which stays open underneath. */}
      <IntegrationExplorerDrawer
        open={isIntegrationDrawerOpen}
        onClose={() => setIsIntegrationDrawerOpen(false)}
        integrationAlias={editNodeDetails?.integration_alias}
        defaultObjectName={editNodeDetails?.integration_object}
        accent="#FFDE59"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: '10px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AgentFlowEditor;
